import { useEffect, useState } from "react";
import { tokens } from "../../theme";
import {
  Box,
  Fab,
  Paper,
  Tabs,
  Tab,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { Science as ScienceIcon, Close as CloseIcon, AdminPanelSettings } from "@mui/icons-material";
import { devService, DevPersona, DevTipo } from "../../services/devService";

function appHref(): string {
  const base = ((import.meta as any).env?.BASE_URL || "/").replace(/\/$/, "");
  return `${base}/app`;
}

/**
 * Switcher de personalidad SOLO para desarrollo: permite "convertirse" en
 * cualquier Persona real (estudiante/docente) o en coordinador, para probar las
 * vistas con datos reales. Se auto-oculta si el backend no lo permite (404).
 */
export default function DevSwitcher() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState<DevTipo>("estudiante");
  const [q, setQ] = useState("");
  const [personas, setPersonas] = useState<DevPersona[]>([]);
  const [busy, setBusy] = useState(false);

  // ¿dev-login habilitado? El endpoint responde 404 si no.
  useEffect(() => {
    devService
      .listPersonas("estudiante")
      .then(() => setEnabled(true))
      .catch(() => setEnabled(false));
  }, []);

  // Carga la lista al abrir / cambiar tipo / buscar (con debounce).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      devService
        .listPersonas(tipo, q)
        .then(setPersonas)
        .catch(() => setPersonas([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, tipo, q]);

  const become = async (payload: { rut: number } | { coordinador: true }) => {
    setBusy(true);
    try {
      await devService.loginAs(payload);
      window.location.href = appHref();
    } catch {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  if (!open) {
    return (
      <Fab
        size="medium"
        onClick={() => setOpen(true)}
        aria-label="Dev switcher"
        sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1400, bgcolor: "secondary.main", color: tokens.neutro.blanco, "&:hover": { bgcolor: "secondary.dark" } }}
      >
        <ScienceIcon />
      </Fab>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{ position: "fixed", bottom: 16, right: 16, width: 330, maxHeight: "72vh", display: "flex", flexDirection: "column", borderRadius: 2, overflow: "hidden", zIndex: 1400 }}
    >
      <Box sx={{ bgcolor: "secondary.main", color: tokens.neutro.blanco, px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <ScienceIcon fontSize="small" />
        <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>Dev · cambiar personalidad</Typography>
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: tokens.neutro.blanco }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Button startIcon={<AdminPanelSettings />} onClick={() => become({ coordinador: true })} disabled={busy} sx={{ m: 1 }}>
        Entrar como Coordinador (ve todo)
      </Button>
      <Divider />

      <Tabs value={tipo} onChange={(_, v) => setTipo(v as DevTipo)} variant="fullWidth">
        <Tab label="Estudiantes" value="estudiante" />
        <Tab label="Docentes" value="docente" />
      </Tabs>

      <TextField
        size="small"
        placeholder="Buscar nombre o RUT…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ m: 1 }}
      />

      <List dense sx={{ overflowY: "auto" }}>
        {personas.map((p) => (
          <ListItemButton key={p.rut} onClick={() => become({ rut: p.rut })} disabled={busy}>
            <ListItemText
              primary={p.nombre}
              secondary={`RUT ${p.rut} · ${p.n} ${tipo === "docente" ? "guiadas" : "instancias"}`}
            />
          </ListItemButton>
        ))}
        {personas.length === 0 && (
          <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
            Sin resultados.
          </Typography>
        )}
      </List>
    </Paper>
  );
}
